import {FieldProps, WdiCardTitle, wdiCore, WdiInput, WdiModalAppProps, WdiModalProps} from "@/components/WdiControls";
import {WdiModalForm} from "@/components/WdiControls/WdiModalForm";
import {Form, message, Table, Typography} from "antd";
import React, {useEffect, useRef, useState} from "react";
import {ProFormInstance} from "@ant-design/pro-form";
import {WdiInputNumber} from "@/components/WdiControls/WdiInputNumber";
import {WdiSelectFormat, WdiTreeSelect} from "@/components/WdiControls/WdiSelect";
import {ResponseStatus} from "@/utils/request";
import DataStore from "@/components/GridWidget/DataStore";
import {WdiPanel} from "@/components/WdiControls/WdiPanel";
import {WdiTable} from "@/components/WdiControls/WdiTable";
import {ImportModalApp} from "@/components/WdiControls/WdiImport";
import {BO} from "@/services/mdm/bo";
import {Enum} from "@/enums/system";
import {ToExcelProps} from "@/components/WdiControls/WdiExcel";

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  record: any;
  index: number;
  children: React.ReactNode;
}

const FormSaMktDetailAllocateCcEditApp: React.FC<WdiModalAppProps> = (props) => {
  const formRef = useRef<ProFormInstance>();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [detail, setDetail] = useState<API.BOVo>();
  const editingDetail = useRef<API.BOVo>();
  const [editingKey, setEditingKey] = useState('');
  const [importModalProps, setImportModalProps] = useState<WdiModalProps>({visible: false});
  const {allocateCc} = props.data;
  const [importExcels, setImportExcels] = useState<ToExcelProps[]>([]);

  const onImportClose = () => {
    setImportModalProps({visible: false, title: "", id: null});
  }
  const onImportSave = (uploadResult: any) => {
    if (uploadResult.code == ResponseStatus.SUCCESS) {
      setDataSource(DataStore.init(uploadResult?.data?.data));
    }
    setImportModalProps({visible: false, title: "", id: null, data: null});
  }
  const isEditing = (record: any) => {
    return record.uid === editingKey;
  };
  const edit = (record: Partial<any> & { key: React.Key }) => {
    BO.get(record.boId).then(res => {
      const newDetail: API.BOVo = {
        boId: record.boId,
        boName: res.data.boName,
        uid: record.uid,
        shareRate: record.shareRate,
        remark: record.remark
      };

      editingDetail.current = newDetail;
      formRef.current?.setFieldsValue({
        boId: {
          value: newDetail?.boId,
          label: newDetail?.boName,
        },
        shareRate: newDetail.shareRate,
        remark: newDetail.remark
      });
      setEditingKey(record.uid);
    });
  };
  const validateEditItem = () => {
    if (!editingDetail.current?.boId) {
      message.error('使用部门不能为空');
      return false;
    }
    if (!editingDetail.current?.shareRate) {
      message.error('分摊比例不能为空');
      return false;
    }
    if (dataSource.findIndex(item => {
      return item.boId == editingDetail.current?.boId && item.uid != editingDetail.current?.uid;
    }) >= 0) {
      message.error(`使用部门【${editingDetail.current?.boName}】已存在，请检查`);
      return false;
    }
    return true;
  };
  const validateItem = () => {
    if (!detail?.boId) {
      message.error('使用部门不能为空');
      return false;
    }
    if (!detail?.shareRate) {
      message.error('分摊比例不能为空');
      return false;
    }
    if (dataSource.findIndex(item => {
      return item.boId == detail.boId;
    }) >= 0) {
      message.error(`使用部门【${detail.boName}】已存在，请检查`);
      return false;
    }
    return true;
  };
  const addItem = () => {
    if (!validateItem()) {
      return false;
    }
    setDataSource(DataStore.add(dataSource, detail));
    setDetail(undefined);
    return true;
  };
  const cancel = () => {
    setEditingKey('');
  };
  const save = async (key: React.Key) => {
    if (!validateEditItem()) {
      return false;
    }

    const row = editingDetail.current;
    const newData = [...dataSource];
    const index = newData.findIndex(item => key === item.uid);
    if (index > -1) {
      const item = newData[index];
      newData.splice(index, 1, {
        ...item,
        ...row,
      });
    } else {
      newData.push(row);
    }
    setDataSource(newData);
    setEditingKey('');
    editingDetail.current = undefined;
    return true;
  };
  const del = (key: any) => {
    if (window.confirm('确认要删除吗')) {
      setDataSource(DataStore.del(dataSource, key));
    }
  };

  const columns = [
    {
      title: '使用部门',
      dataIndex: 'boId',
      editable: true,
      ellipsis: true,
      width: 200,
      render: (value: any, record: any) => {
        return record.boName;
      },
    },
    {
      title: '分摊比例',
      dataIndex: 'shareRate',
      editable: true,
      ellipsis: true,
      width: 160,
      render: (value: any, record: any) => {
        return wdiCore.Number.RateToString(record.shareRate, 4);
      },
    },
    {
      title: '备注',
      dataIndex: 'remark',
      editable: true,
      ellipsis: true,
    },
    {
      title: '操作',
      dataIndex: 'operation',
      width: 80,
      render: (_: any, record: any) => {
        const editable = isEditing(record);
        return editable ? (
          <>
            <Typography.Link onClick={() => save(record.uid)} style={{marginRight: 8}}>
              更新
            </Typography.Link>
            <Typography.Link onClick={() => cancel()}>
              取消
            </Typography.Link>
          </>
        ) : (
          <>
            <Typography.Link disabled={editingKey !== ''} onClick={() => edit(record)} style={{marginRight: 8}}>
              编辑
            </Typography.Link>
            <Typography.Link onClick={() => del(record.uid)}>
              删除
            </Typography.Link>
          </>);
      },
    },
  ];

  const mergedColumns = columns.map(col => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: any) => ({
        record,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });


  const EditableCell: React.FC<EditableCellProps> = ({
                                                       editing,
                                                       dataIndex,
                                                       title,
                                                       record,
                                                       index,
                                                       children,
                                                       ...restProps
                                                     }) => {
    let inputNode = children;
    if (dataIndex == 'boId') {
      inputNode = <WdiTreeSelect style={{width: "200%"}} {...{
        placeholder: "请选择使用部门",
        //defaultExpandKeys: expandKeysBo,
        dataLoading: (params) => {
          return BO.search(Object.assign({
              isActive: true,
              boLevels: [Enum.BOLevel.BO1, Enum.BOLevel.BO2, Enum.BOLevel.BO3, Enum.BOLevel.BO4].join(),
              withPermissions: true
            },
            params)).then((response) => {
            const {treeData, dict} = FieldProps.TreeSelect.formatTreeData({
              textField: "boName",
              valueField: "boId",
              id: "boId",
              pId: "parentId"
            }, response.data);
            return {treeData, dict};
          })
        },
        onChange: async (value) => {
          let newDetail: API.BOVo = {...editingDetail.current};
          if (value) {
            const boId = WdiSelectFormat.getvalue(value)
            const ccEditResponse = await BO.get(boId);
            if (ccEditResponse.code == ResponseStatus.SUCCESS) {
              const bo = ccEditResponse.data;
              if (bo) {
                newDetail = Object.assign({}, newDetail, {
                  boId: bo.boId,
                  boName: bo.boName,
                });
              }
            }
          } else {
            newDetail = Object.assign({}, newDetail, {
              boId: undefined,
              boName: undefined,
            });
          }
          editingDetail.current = newDetail;
        },

      }} />;
    } else if (dataIndex == 'shareRate') {
      inputNode = <WdiInputNumber style={{width: "200%"}} format='p' digits={4} min={0}
                                  onBlur={(e: any) => {
                                    console.log("shareRate", "onBlur");
                                    const shareRate = wdiCore.Number.AmountToString(e.target.value / 100, 6);
                                    editingDetail.current = Object.assign({}, editingDetail.current, {shareRate: shareRate});
                                  }}
      />;
    } else if (dataIndex == 'remark') {
      inputNode = <WdiInput style={{width: "200%"}}
                            onBlur={(e: any) => {
                              console.log("remark", "onBlur");
                              const remark = e.target.value;
                              editingDetail.current = Object.assign({}, editingDetail.current, {remark: remark});
                            }}
      />;
    }
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{margin: 0}}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };
  useEffect(() => {
    if (allocateCc) {
      setDataSource(DataStore.init(allocateCc))
    }
  }, [props.data]);

  useEffect(() => {
    const boSheet = {
      dataLoading: async () => {
        return await BO.exportEx({withPermissions: true, isActive: true, boLevels: String(Enum.BOLevel.BO4)});
      },
      data: null,
      names: [
        {name: "boName4", displayName: "四级使用部门"},
        {name: "boName3", displayName: "三级使用部门"},
        {name: "boName2", displayName: "二级使用部门"},
        {name: "boName1", displayName: "一级使用部门"}
      ],
      sheetName: "使用部门"
    };
    setImportExcels([
      {
        name: '全部下载',
        sheetProps: [boSheet],
      }, {
        name: '使用部门列表',
        sheetProps: [boSheet],
      }
    ]);
  }, []);
  return (
    <>
      <WdiModalForm
        formRef={formRef}
        labelCol={{span: 6}}
        wrapperCol={{span: 12}}
        labelAlign='left'
        layout="horizontal"
        title={props.title}
        visible={props.visible}
        modalProps={{
          onCancel: () => {
            props.onClose ? props.onClose() : null
          },
          destroyOnClose: true,
          maskClosable: false
        }}
        onFinish={async (values) => {
          const {...entity} = values;

          let details = [...dataSource];
          if (!details || details.length <= 0) {
            message.error('请填写分摊信息！');
            return;
          }
          if (detail && detail.boId) {
            if (!validateItem()) {
              return;
            }
            details = DataStore.add(dataSource, detail);
          }

          if (editingDetail.current && editingDetail.current?.boId) {
            if (!validateEditItem()) {
              return;
            }
            details = DataStore.edit(dataSource, editingDetail.current);
          }
          // Function to validate the shareRate sum
          const validateShareRate = (details: any[]) => {
            let totalShareRate = 0;
            for (const item of details) {
              const shareRate = wdiCore.Number.ToNumber(item.shareRate, 6) || 0;
              totalShareRate = wdiCore.Number.ToNumber(totalShareRate + shareRate, 6)
              if (totalShareRate > 1) {
                return false;
              }
            }
            return Math.abs(totalShareRate - 1) < Number.EPSILON;
          };

          if (!validateShareRate(details)) {
            message.error('分摊比例之和必须为100%!');
            return;
          }
          details.forEach(item => {
            item.shareRate = wdiCore.Number.ToNumber(item?.shareRate);
          })
          entity.ccShareTemplateDetails = details;

          if (props.onSave) {
            props.onSave?.({code: ResponseStatus.SUCCESS, data: dataSource});
          }
          return true;
        }}
      >
        <WdiPanel
          title={< WdiCardTitle title='分摊信息'/>}
          collapsed={false}
          extra={
            <>
              <a type='primary' onClick={(e) => {
                setImportModalProps({title: '使用部门分摊模板', visible: true, data: dataSource});
              }}>
                导入
              </a>
            </>
          }
        >
          <WdiTable
            components={{
              body: {
                cell: EditableCell,
              },
            }}
            columns={mergedColumns}
            rowKey='uid'
            dataSource={dataSource}
            pagination={false}
            aggrColumns={['shareRate']}
            showAggrType={'p'}
            digits={4}
            extraFooterSummary={() => {
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <WdiTreeSelect
                      style={{width: "100%"}}
                      value={{
                        value: detail?.boId,
                        label: detail?.boName,
                      }}
                      {...{
                        placeholder: "请选择使用部门",
                        //defaultExpandKeys: expandKeysBo,
                        dataLoading: (params) => {
                          return BO.search(Object.assign({
                            isActive: true,
                            boLevels: [Enum.BOLevel.BO1, Enum.BOLevel.BO2, Enum.BOLevel.BO3, Enum.BOLevel.BO4].join(),
                            withPermissions: true
                          }, params)).then((response) => {
                            const {treeData, dict} = FieldProps.TreeSelect.formatTreeData({
                              textField: "boName",
                              valueField: "boId",
                              id: "boId",
                              pId: "parentId"
                            }, response.data);
                            return {treeData, dict};
                          })
                        },
                        onChange: async (value) => {
                          let newDetail: API.BOVo = {...editingDetail.current};
                          if (value) {
                            const boId = WdiSelectFormat.getvalue(value)
                            const ccEditResponse = await BO.get(boId);
                            if (ccEditResponse.code == ResponseStatus.SUCCESS) {
                              const bo = ccEditResponse.data;
                              if (bo) {
                                newDetail = Object.assign({}, newDetail, {
                                  boId: bo.boId,
                                  boName: bo.boName,
                                });
                              }
                            }
                          } else {
                            newDetail = Object.assign({}, newDetail, {
                              boId: undefined,
                              boName: undefined,
                            });
                          }
                          setDetail(newDetail);
                        },

                      }} />
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <WdiInputNumber
                      format='p' digits={4} min={0} value={detail?.shareRate}
                      onBlur={(e: any) => {
                        const shareRate = wdiCore.Number.AmountToString(e.target.value / 100, 6);
                        setDetail(Object.assign({}, detail, {shareRate: shareRate}));
                      }}
                    />
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    <WdiInput value={detail?.remark}
                              onBlur={(e: any) => {
                                const remark = e.target.value;
                                setDetail(Object.assign({}, detail, {remark: remark}));
                              }}
                    />
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>
                    <Typography.Link onClick={() => addItem()}>
                      新增
                    </Typography.Link>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </WdiPanel>
      </WdiModalForm>
      {importModalProps.visible &&
        <ImportModalApp
          title={importModalProps.title}
          visible={importModalProps.visible}
          settingsUrl="/expenseapi/saMktButtons/getBoImportSettings"
          uploadingUrl="/expenseapi/saMktButtons/boImportUploading"
          onClose={onImportClose}
          onSave={onImportSave}
          clientParameters={{
            details: JSON.stringify(dataSource)
          }}
          importExcels={importExcels}
        />

      }
    </>
  )
}
export default FormSaMktDetailAllocateCcEditApp;
